import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY } from '../../theme';
import { SignInChip } from '../common';
import { useChecklistStore } from '../../store/checklistStore';
import { useAuthStore } from '../../store/authStore';

interface EvaluatorBannerProps {
  visible: boolean;
}

/**
 * Sits above AddressHeader when an insurance agent arrives via a
 * `?view=evaluator` link — a reminder that this is the owner's page, not
 * theirs, and that everything below is read only. The insurer name comes
 * from the resolved checklist (set by PropertyChecklistSection), same
 * source CoverageStrip reads.
 */
export function EvaluatorBanner({ visible }: EvaluatorBannerProps) {
  const { colors } = useTheme();
  const selectedChecklist = useChecklistStore((s) => s.selectedChecklist);
  const user = useAuthStore((s) => s.user);

  if (!visible) return null;

  const insurerName = selectedChecklist
    ? (selectedChecklist.displayName ?? selectedChecklist.insurerName)
    : "the owner's insurer";

  return (
    <View
      style={[styles.card, { borderColor: colors.border, backgroundColor: colors.surface }]}
      testID="structure-evaluator-banner">
      <Text style={[styles.text, { color: colors.textSecondary }]}>
        You are viewing this property as an evaluator for {insurerName}. This is a read only view of
        what the owner has documented.
      </Text>
      {!user && (
        <View style={styles.signInRow}>
          <Text style={[styles.text, { color: colors.textSecondary }]}>
            Sign in to see checklist progress.
          </Text>
          <SignInChip variant="bare" />
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: BORDER_RADIUS.md,
    padding: SPACING.smd,
    gap: SPACING.sm,
  },
  text: {
    ...TYPOGRAPHY.bodySmall,
  },
  signInRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
  },
});
